import { BED_JOINT_GAP_MM, EPSILON } from './constants.js';
import type { Mm } from './units.js';
import { quantise } from './units.js';
import { fail } from './errors.js';

/**
 * Sketch geometry: the outline a user draws before, or instead of, laying
 * bricks by hand.
 *
 * WALL lines are part of the real wall — they carry the outline and bound the
 * shapes a bond is filled into. CONSTRUCTION lines are guides: drawn faintly,
 * never filled, but snapped to just the same.
 */
export type LineKind = 'wall' | 'construction';

/**
 * What a closed shape represents.
 *
 * Windows and doors are holes in the wall. Flint is not a hole: it is the same
 * wall built of something else, so it takes no bricks for the same reason an
 * opening takes none, and is then filled in solid rather than left open.
 */
export type ShapeRole = 'wall' | 'window' | 'door' | 'flint';

export interface SketchPoint {
  readonly x: Mm;
  readonly y: Mm;
}

export interface SketchSegment {
  readonly id: string;
  readonly kind: LineKind;
  readonly a: SketchPoint;
  readonly b: SketchPoint;
}

export interface SketchShape {
  readonly id: string;
  readonly role: ShapeRole;
  readonly kind: LineKind;
  /** A closed ring; the last point joins back to the first implicitly. */
  readonly points: readonly SketchPoint[];
}

export interface Sketch {
  readonly segments: readonly SketchSegment[];
  readonly shapes: readonly SketchShape[];
}

export const EMPTY_SKETCH: Sketch = { segments: [], shapes: [] };

export const isEmptySketch = (sketch: Sketch): boolean =>
  sketch.segments.length === 0 && sketch.shapes.length === 0;

export const point = (x: number, y: number): SketchPoint => ({ x: quantise(x), y: quantise(y) });

/** Segments of a shape's ring, for drawing and for snapping. */
export function shapeSegments(shape: SketchShape): SketchSegment[] {
  const out: SketchSegment[] = [];
  for (let i = 0; i < shape.points.length; i++) {
    const a = shape.points[i] as SketchPoint;
    const b = shape.points[(i + 1) % shape.points.length] as SketchPoint;
    out.push({ id: `${shape.id}:${i}`, kind: shape.kind, a, b });
  }
  return out;
}

/** Every line in a sketch, loose segments and shape edges alike. */
export function allSegments(sketch: Sketch): SketchSegment[] {
  const out = [...sketch.segments];
  for (const shape of sketch.shapes) out.push(...shapeSegments(shape));
  return out;
}

/** Axis-aligned extent of sketch geometry. */
export interface SketchRect {
  readonly minX: Mm;
  readonly minY: Mm;
  readonly maxX: Mm;
  readonly maxY: Mm;
}

export function shapeBounds(shape: SketchShape): SketchRect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of shape.points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

export function sketchBounds(sketch: Sketch): SketchRect | null {
  const segments = allSegments(sketch);
  if (segments.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const { a, b } of segments) {
    minX = Math.min(minX, a.x, b.x);
    minY = Math.min(minY, a.y, b.y);
    maxX = Math.max(maxX, a.x, b.x);
    maxY = Math.max(maxY, a.y, b.y);
  }
  return { minX, minY, maxX, maxY };
}

/** Signed area doubled; positive when the ring winds counter-clockwise. */
export function ringArea2(points: readonly SketchPoint[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i] as SketchPoint;
    const b = points[(i + 1) % points.length] as SketchPoint;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum;
}

/** Ray casting, with points exactly on an edge counted as inside. */
export function pointInPolygon(points: readonly SketchPoint[], x: Mm, y: Mm): boolean {
  if (points.length < 3) return false;
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i] as SketchPoint;
    const b = points[j] as SketchPoint;
    if (pointOnSegment(a, b, x, y)) return true;
    const intersects =
      a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

export function pointOnSegment(a: SketchPoint, b: SketchPoint, x: Mm, y: Mm): boolean {
  const cross = (b.x - a.x) * (y - a.y) - (b.y - a.y) * (x - a.x);
  if (Math.abs(cross) > 1e-6) return false;
  return (
    x >= Math.min(a.x, b.x) - EPSILON &&
    x <= Math.max(a.x, b.x) + EPSILON &&
    y >= Math.min(a.y, b.y) - EPSILON &&
    y <= Math.max(a.y, b.y) + EPSILON
  );
}

const CROSS_EPSILON = 1e-6;

/**
 * PROPER intersection: the segments genuinely cross, rather than merely
 * touching or lying along one another.
 *
 * The distinction matters everywhere here — a brick laid flush against the
 * outline shares an edge with it, and treating that as a crossing would reject
 * exactly the bricks a filled wall is made of.
 */
function segmentsCross(
  p1: SketchPoint,
  p2: SketchPoint,
  p3: SketchPoint,
  p4: SketchPoint,
): boolean {
  const d = (a: SketchPoint, b: SketchPoint, c: SketchPoint): number =>
    (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  const opposite = (a: number, b: number): boolean =>
    (a > CROSS_EPSILON && b < -CROSS_EPSILON) || (a < -CROSS_EPSILON && b > CROSS_EPSILON);
  return (
    opposite(d(p3, p4, p1), d(p3, p4, p2)) && opposite(d(p1, p2, p3), d(p1, p2, p4))
  );
}

/**
 * Exact for a simple polygon: every corner inside, and no edge crossing the
 * rectangle. The corner test alone would admit a brick straddling a concave
 * notch.
 */
export function rectInsidePolygon(
  points: readonly SketchPoint[],
  x: Mm,
  y: Mm,
  width: Mm,
  height: Mm,
): boolean {
  const corners: SketchPoint[] = [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];
  for (const corner of corners) {
    if (!pointInPolygon(points, corner.x, corner.y)) return false;
  }
  for (let i = 0; i < points.length; i++) {
    const a = points[i] as SketchPoint;
    const b = points[(i + 1) % points.length] as SketchPoint;
    for (let c = 0; c < 4; c++) {
      const p = corners[c] as SketchPoint;
      const q = corners[(c + 1) % 4] as SketchPoint;
      if (segmentsCross(a, b, p, q)) return false;
    }
  }
  return true;
}

/**
 * Whether a rectangle's INTERIOR meets a polygon's interior.
 *
 * Tested against a slightly inset rectangle, so a brick laid flush against a
 * window reveal is outside the opening rather than in it.
 */
export function rectOverlapsPolygon(
  points: readonly SketchPoint[],
  x: Mm,
  y: Mm,
  width: Mm,
  height: Mm,
): boolean {
  const inset = Math.min(0.01, width / 4, height / 4);
  const left = x + inset;
  const bottom = y + inset;
  const right = x + width - inset;
  const top = y + height - inset;
  const corners: SketchPoint[] = [
    { x: left, y: bottom },
    { x: right, y: bottom },
    { x: right, y: top },
    { x: left, y: top },
  ];
  for (const corner of corners) {
    if (pointInPolygon(points, corner.x, corner.y)) return true;
  }
  for (const p of points) {
    if (p.x > left && p.x < right && p.y > bottom && p.y < top) return true;
  }
  for (let i = 0; i < points.length; i++) {
    const a = points[i] as SketchPoint;
    const b = points[(i + 1) % points.length] as SketchPoint;
    for (let c = 0; c < 4; c++) {
      const p = corners[c] as SketchPoint;
      const q = corners[(c + 1) % 4] as SketchPoint;
      if (segmentsCross(a, b, p, q)) return true;
    }
  }
  return false;
}

/** The closest point on a segment to an arbitrary point. */
export function closestPointOnSegment(
  a: SketchPoint,
  b: SketchPoint,
  x: Mm,
  y: Mm,
): SketchPoint {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq <= EPSILON) return a;
  const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (y - a.y) * dy) / lengthSq));
  return { x: a.x + dx * t, y: a.y + dy * t };
}

/** Where a segment sits at a given height, or null if it never does. */
export function xOnSegmentAtY(a: SketchPoint, b: SketchPoint, y: Mm): Mm | null {
  if (Math.abs(b.y - a.y) <= EPSILON) return null;
  const t = (y - a.y) / (b.y - a.y);
  if (t < -EPSILON || t > 1 + EPSILON) return null;
  return a.x + (b.x - a.x) * t;
}

/** Where a segment sits at a given horizontal position, or null. */
export function yOnSegmentAtX(a: SketchPoint, b: SketchPoint, x: Mm): Mm | null {
  if (Math.abs(b.x - a.x) <= EPSILON) return null;
  const t = (x - a.x) / (b.x - a.x);
  if (t < -EPSILON || t > 1 + EPSILON) return null;
  return a.y + (b.y - a.y) * t;
}

/** True when a segment leans more vertical than horizontal. */
export const isMoreVertical = (a: SketchPoint, b: SketchPoint): boolean =>
  Math.abs(b.y - a.y) >= Math.abs(b.x - a.x);

/**
 * Whether a ring crosses itself.
 *
 * An even-odd fill of a self-intersecting ring comes out with holes nobody
 * drew, so a bow-tie has to be refused at the point of closing rather than
 * quietly mis-filled later.
 */
export function ringSelfIntersects(points: readonly SketchPoint[]): boolean {
  const n = points.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a1 = points[i] as SketchPoint;
    const a2 = points[(i + 1) % n] as SketchPoint;
    for (let j = i + 1; j < n; j++) {
      // Adjacent edges share a vertex; they touch by construction.
      if (j === i || (j + 1) % n === i || (i + 1) % n === j) continue;
      const b1 = points[j] as SketchPoint;
      const b2 = points[(j + 1) % n] as SketchPoint;
      if (segmentsCross(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}

let nextId = 1;
export const sketchId = (prefix: string): string => `${prefix}${nextId++}`;
/** Reset the id counter, so tests and fixtures are reproducible. */
export const resetSketchIds = (): void => {
  nextId = 1;
};

/** Close a drawn chain into a shape. Needs at least a triangle. */
export function closeShape(
  points: readonly SketchPoint[],
  role: ShapeRole,
  kind: LineKind = 'wall',
): SketchShape {
  const ring = dedupeRing(points);
  if (ring.length < 3) fail('A closed shape needs at least three points');
  if (Math.abs(ringArea2(ring)) < EPSILON) fail('A closed shape needs an area');
  if (ringSelfIntersects(ring)) fail('A closed shape cannot cross itself');
  return { id: sketchId('s'), role, kind, points: ring };
}

function dedupeRing(points: readonly SketchPoint[]): SketchPoint[] {
  const ring: SketchPoint[] = [];
  for (const p of points) {
    const last = ring[ring.length - 1];
    if (last !== undefined && Math.abs(last.x - p.x) < EPSILON && Math.abs(last.y - p.y) < EPSILON) {
      continue;
    }
    ring.push(p);
  }
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (
    ring.length > 1 &&
    first !== undefined &&
    last !== undefined &&
    Math.abs(first.x - last.x) < EPSILON &&
    Math.abs(first.y - last.y) < EPSILON
  ) {
    ring.pop();
  }
  return ring;
}

/**
 * The shapes a fill must leave out.
 *
 * Windows, doors and flint panels alike. They are three different things once
 * the wall is standing, but to the course being laid they carry one
 * instruction — no bricks here — and what fills the gap afterwards is decided
 * by the role rather than by the bricklayer.
 */
export const openingShapes = (sketch: Sketch): SketchShape[] =>
  sketch.shapes.filter((shape) => shape.role !== 'wall');

/** The parts of a wall built in flint rather than in brick. */
export const flintShapes = (sketch: Sketch): SketchShape[] =>
  sketch.shapes.filter((shape) => shape.role === 'flint');

/** The shapes a bond can be filled into. */
export const fillableShapes = (sketch: Sketch): SketchShape[] =>
  sketch.shapes.filter((shape) => shape.role === 'wall' && shape.kind === 'wall');

/** Course baselines that fit inside a shape's vertical extent. */
export function courseBaselinesFor(bounds: SketchRect, unitHeight: Mm): Mm[] {
  const pitch = unitHeight + BED_JOINT_GAP_MM;
  const out: Mm[] = [];
  for (let y = bounds.minY; y + unitHeight <= bounds.maxY + EPSILON; y += pitch) {
    out.push(quantise(y));
  }
  return out;
}
